import type { V2CreateAdminRepresentativeInput } from '@idc-forms/schemas/api-v2/admin-representatives'
import { CivilStatusLabel } from '@idc-forms/types/civil-status'
import { GenderLabel } from '@idc-forms/types/gender'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import countries from '@/shared/catalogs/countries.json'
import { hasLocalPlaceDetail, localPlaceNames } from '@/shared/catalogs/geo'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDate, formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteRepresentativeV2Mutation,
  useUpdateRepresentativeV2Mutation
} from '../hooks/use-representative-v2-mutations'
import type { RepresentativeV2AdminRow } from '../models/representative-admin-v2'
import { RepresentativeV2Form } from './representative-v2-form'
import { formatIdentification, RepresentativeActiveBadge } from './representatives-admin-columns'

type CountryOption = { isoCode: string; name: { es: string } }

const COUNTRY_OPTIONS = countries as CountryOption[]

const countryName = (isoCode?: string | null) =>
  COUNTRY_OPTIONS.find((c) => c.isoCode === isoCode)?.name.es ?? isoCode ?? '—'

/** Fecha plana YYYY-MM-DD → humana, sin pasar por zonas horarias. */
const humanPlainDate = (value?: string | null) =>
  value ? formatHumanDate(`${value}T00:00:00`) : '—'

type RepresentativeV2DetailPanelProps = {
  representative: RepresentativeV2AdminRow
}

/**
 * Panel de detalle del representante legal contra la API v2. Espeja el panel v1
 * —mismo `RepresentativeV2Form`, mismo badge de estado, mismos acordeones— y sólo
 * cambia el transporte: direcciona por `rep_`, la sociedad llega como
 * `{ id: soc_, name }` y la autoría como `{ id: usr_, fullname }`.
 */
export function RepresentativeV2DetailPanel({ representative }: RepresentativeV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateRepresentativeV2Mutation()
  const deleteMutation = useDeleteRepresentativeV2Mutation()
  const { has } = usePermissions()

  const toggleActive = () => {
    updateMutation.mutate({ id: representative.id, body: { active: !representative.active } })
  }

  if (mode === 'edit') {
    return (
      <RepresentativeV2Form
        defaultValues={{
          // El SDK tipa el id de sociedad como string plano; el form usa el branded
          // del schema (`soc_`). Mismo dato en runtime.
          society: representative.society?.id as
            | V2CreateAdminRepresentativeInput['society']
            | undefined,
          name: representative.name,
          identification: representative.identification,
          gender: representative.gender,
          civilStatus: representative.civilStatus,
          position: representative.position,
          profession: representative.profession,
          // El SDK tipa las fechas de calendario como string plano; el form usa el
          // branded del schema (`LocalDate`). Mismo dato en runtime.
          birthdate: representative.birthdate as V2CreateAdminRepresentativeInput['birthdate'],
          nationality: representative.nationality,
          address: {
            address: representative.address.address,
            zone: representative.address.zone ?? undefined,
            country: representative.address.country,
            department: representative.address.department ?? undefined,
            municipality: representative.address.municipality ?? undefined
          },
          notary: {
            name: representative.notary.name,
            gender: representative.notary.gender
          },
          appointmentMercantileRegister: {
            date: representative.appointmentMercantileRegister
              .date as V2CreateAdminRepresentativeInput['appointmentMercantileRegister']['date'],
            number: representative.appointmentMercantileRegister.number,
            sheet: representative.appointmentMercantileRegister.sheet,
            book: representative.appointmentMercantileRegister.book
          },
          active: representative.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: representative.id, body: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">
              {representative.name}
            </h3>
            {representative.society && (
              <p className="truncate text-sm text-muted-foreground">
                {representative.society.name}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow
              label="Estado"
              value={<RepresentativeActiveBadge active={representative.active} />}
            />
            <DetailFieldRow
              label="DPI"
              value={formatIdentification(representative.identification)}
              copyValue={representative.identification}
            />
            <DetailFieldRow label="Puesto" value={representative.position} />
            <DetailFieldRow label="Sociedad" value={representative.society?.name ?? '—'} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateRepresentants)
              ? [
                  {
                    label: 'Editar representante',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: representative.active
                      ? 'Desactivar representante'
                      : 'Activar representante',
                    icon: representative.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteRepresentants) && !representative.blocked
              ? [
                  {
                    label: 'Eliminar representante',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['personal']}>
            <AccordionItem value="personal">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Datos personales
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Género"
                    value={GenderLabel[representative.gender] ?? representative.gender}
                  />
                  <DetailFieldRow
                    label="Estado civil"
                    value={
                      CivilStatusLabel[representative.civilStatus] ?? representative.civilStatus
                    }
                  />
                  <DetailFieldRow
                    label="Nacimiento"
                    value={humanPlainDate(representative.birthdate)}
                  />
                  <DetailFieldRow
                    label="Nacionalidad"
                    value={countryName(representative.nationality)}
                  />
                  <DetailFieldRow label="Profesión" value={representative.profession} />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="address">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Dirección
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Dirección" value={representative.address.address} />
                  <DetailFieldRow
                    label="País"
                    value={countryName(representative.address.country)}
                  />
                  {hasLocalPlaceDetail(representative.address) && (
                    <DetailFieldRow
                      label="Departamento"
                      value={localPlaceNames(representative.address).department}
                    />
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="notary">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Notario
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Nombre" value={representative.notary.name} />
                  <DetailFieldRow
                    label="Género"
                    value={
                      GenderLabel[representative.notary.gender] ?? representative.notary.gender
                    }
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="appointment">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Nombramiento en registro mercantil
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Fecha"
                    value={humanPlainDate(representative.appointmentMercantileRegister.date)}
                  />
                  <DetailFieldRow
                    label="Número"
                    value={representative.appointmentMercantileRegister.number}
                    copyValue={representative.appointmentMercantileRegister.number}
                  />
                  <DetailFieldRow
                    label="Folio"
                    value={representative.appointmentMercantileRegister.sheet}
                  />
                  <DetailFieldRow
                    label="Libro"
                    value={representative.appointmentMercantileRegister.book}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Creado"
                    value={formatHumanDateTime(representative.createdAt)}
                  />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={representative.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(representative.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={representative.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar representante</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{representative.name}”. Los documentos ya generados conservan sus datos;
              la sociedad queda libre para asignar otro representante.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(representative.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
